/**
 * PublicFormPage — open or OTP-gated public intake form (Quiet Trust theme).
 * Route: /f/:locationId/:slug
 *
 * Flow:
 *   1. Fetch form schema via GET /api/forms/schemas/public/:locationId/:slug
 *   2. authMode = 'open'      → render form immediately (no gate)
 *   3. authMode = 'otp_email' | 'otp_phone' → identity capture →
 *      POST /api/forms/public/request-otp → 6-digit entry →
 *      POST /api/forms/public/verify-otp → returns a short-lived `grant` → unlock form
 *   4. On submit → POST /api/forms/submit (locationId + optional grant in body)
 *   5. AI contact matching runs server-side as part of submit
 *
 * The page mirrors AssignedFormView's patient surface (navy secure banner,
 * single-column container, teal tokens) — it must NOT read like the staff app.
 */

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { forms as formsApi, publicForm, FormSchemaDetail, SubmitterInput } from '../../lib/api.js';
import { flattenFormFields } from '../../lib/form-fields.js';
import { wireFormForUpload } from '../../lib/formio-file-wiring.js';
import { scrollToFirstFormError } from '../../lib/formio-ux.js';
import { IDENTITY_FIELD_CATALOG } from '../../lib/identity-config.js';
import {
  resolveIdentityConfig, otpChannelKey, firstScreen, identityRows, type IdentityFieldKey,
} from '../../lib/public-form-flow.js';
import { substituteIdentityTokens, collectPrefillData } from '@clearintake/shared/identity';
import AIIntakeChat from '../../components/chat/AIIntakeChat.js';

type Screen =
  | { type: 'loading' }
  | { type: 'error'; message: string }
  | { type: 'identity' }
  | { type: 'otp' }
  | { type: 'form' }
  | { type: 'success' };

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const RESEND_COOLDOWN = 60;

// Per-field input metadata for the identity screen. `dob` introduces the app's
// first <input type="date"> (ISO YYYY-MM-DD, matching the identity contract).
const INPUT_TYPE: Record<IdentityFieldKey, string> = {
  firstName: 'text', lastName: 'text', dob: 'date', email: 'email', phone: 'tel',
};
const AUTOCOMPLETE: Record<IdentityFieldKey, string> = {
  firstName: 'given-name', lastName: 'family-name', dob: 'bday', email: 'email', phone: 'tel',
};
const PLACEHOLDER: Partial<Record<IdentityFieldKey, string>> = {
  email: 'you@example.com', phone: '+1 (555) 000-0000',
};

export default function PublicFormPage(): React.ReactElement {
  const { locationId, slug } = useParams<{ locationId: string; slug: string }>();
  const [schema,     setSchema]     = useState<FormSchemaDetail | null>(null);
  const [screen,     setScreen]     = useState<Screen>({ type: 'loading' });
  // Live identity inputs, keyed by field key (firstName/lastName/dob/email/phone).
  const [values,     setValues]     = useState<Record<string, string>>({});
  // Committed identity — set once at the identity-step transition so the wired
  // schema memo (token prefill) re-fires exactly once, never per keystroke.
  const [submitter,  setSubmitter]  = useState<SubmitterInput>({});
  const [submitting, setSubmitting] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [grant,      setGrant]      = useState<string | null>(null);
  const [maskedContact, setMaskedContact] = useState('');
  const [identityError, setIdentityError] = useState<string | null>(null);
  const [formError,  setFormError]  = useState<string | null>(null);
  const [formData,   setFormData]   = useState<Record<string, unknown>>({});
  const [mode,       setMode]       = useState<'ai' | 'manual'>('ai');
  const [FormComp,   setFormComp]   = useState<React.ComponentType<{
    form: unknown;
    submission?: { data: Record<string, unknown> };
    onSubmit: (s: { data: Record<string, unknown> }) => void;
    onError?: (errors: unknown) => void;
    options?: Record<string, unknown>;
  }> | null>(null);

  useEffect(() => {
    import('@formio/react').then((mod) => {
      setFormComp(() => (mod as { Form: typeof FormComp }).Form);
    }).catch(() => {/* no formio */});
  }, []);

  useEffect(() => {
    if (!locationId || !slug) {
      setScreen({ type: 'error', message: 'This form link looks incomplete. Please check the link and try again.' });
      return;
    }
    formsApi.schemas.getPublic(locationId, slug).then((res) => {
      if (res.success && res.data) {
        setSchema(res.data);
        setMode(res.data.aiMode === 'form_only' ? 'manual' : 'ai');
        // OTP always → identity; open routes to identity only when the config
        // asks for fields ([] → straight to the form, unchanged).
        const cfg = resolveIdentityConfig(res.data.identityFields);
        setScreen({ type: firstScreen(res.data.authMode, cfg) });
      } else {
        setScreen({ type: 'error', message: res.error ?? 'This form could not be found.' });
      }
    }).catch(() => setScreen({ type: 'error', message: 'We could not load this form. Please check your connection and try again.' }));
  }, [locationId, slug]);

  const authMode = schema?.authMode ?? 'open';
  const config   = useMemo(() => resolveIdentityConfig(schema?.identityFields), [schema?.identityFields]);
  const rows     = useMemo(() => identityRows(authMode, config), [authMode, config]);
  const otpKey   = otpChannelKey(authMode);
  const maxDob   = new Date().toISOString().slice(0, 10);

  const labelFor = (key: IdentityFieldKey): string =>
    IDENTITY_FIELD_CATALOG.find((o) => o.key === key)?.label ?? key;

  // The OTP contact value — the channel row on the identity screen doubles as it.
  const channelContact = (): string => (otpKey ? (values[otpKey] ?? '').trim() : '');

  // --- Identity step: validate configured fields, then request OTP / advance --
  const handleIdentitySubmit = useCallback(async () => {
    if (!schema || !locationId) return;
    setIdentityError(null);

    for (const row of rows) {
      const v = (values[row.key] ?? '').trim();
      if (!v) {
        if (row.required) { setIdentityError(`Please enter your ${labelFor(row.key).toLowerCase()}.`); return; }
        continue;
      }
      if (row.key === 'email' && !EMAIL_RE.test(v)) {
        setIdentityError('Please enter a valid email address.'); return;
      }
      if (row.key === 'phone' && v.replace(/\D/g, '').length < 10) {
        setIdentityError('Please enter a valid phone number.'); return;
      }
    }

    // Commit the identity (configured keys only) — drives client-side {{token}}
    // prefill via the wired-schema memo below.
    const built: SubmitterInput = {};
    for (const f of config) {
      const v = (values[f.key] ?? '').trim();
      if (v) built[f.key] = v;
    }
    setSubmitter(built);

    // Open forms (no OTP channel) go straight to the form once identity is captured.
    if (!otpKey) { setScreen({ type: 'form' }); return; }

    setRequesting(true);
    try {
      const res = await publicForm.requestOtp({ locationId, formId: schema.id, channel: otpKey, contact: channelContact() });
      if (res.success && res.data) {
        setMaskedContact(res.data.masked);
        setScreen({ type: 'otp' });
      } else {
        setIdentityError(res.error ?? 'We could not send a verification code. Please try again.');
      }
    } catch {
      setIdentityError('Network error — please check your connection and try again.');
    } finally {
      setRequesting(false);
    }
  }, [schema, locationId, rows, config, values, otpKey]);

  // --- OTP verify: throws on failure so OtpEntry clears + shows inline error --
  const handleVerifyOtp = useCallback(async (code: string) => {
    if (!schema || !locationId || !otpKey) throw new Error('Form not ready');
    const res = await publicForm.verifyOtp({ locationId, formId: schema.id, channel: otpKey, contact: channelContact(), otp: code });
    if (res.success && res.data) {
      setGrant(res.data.grant);
      setScreen({ type: 'form' });
    } else {
      throw new Error(res.error ?? 'That code was not correct. Please try again.');
    }
  }, [schema, locationId, values, otpKey]);

  const handleResendOtp = useCallback(async () => {
    if (!schema || !locationId || !otpKey) return;
    await publicForm.requestOtp({ locationId, formId: schema.id, channel: otpKey, contact: channelContact() });
  }, [schema, locationId, values, otpKey]);

  // --- Submit -----------------------------------------------------------------
  const handleFormSubmit = useCallback(async (submission: { data: Record<string, unknown> }) => {
    if (!schema || !locationId) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const res = await formsApi.submit({
        formId: schema.id,
        locationId,
        data: submission.data,
        grant: grant ?? undefined,
        // Patient-entered owner identity (server stamps source:'patient'). Omitted
        // when no identity was captured (e.g. an open form with a []-config).
        submitter: Object.keys(submitter).length ? submitter : undefined,
      });
      if (res.success) setScreen({ type: 'success' });
      else setFormError(res.error ?? "We couldn't submit your form. Please try again.");
    } catch {
      setFormError('Network error — please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }, [schema, locationId, grant, submitter]);

  // --- Form region (honours aiMode) ------------------------------------------
  // Recursively flatten the Form.io schema (panels/columns/etc.) into leaf input
  // fields — mirrors the server so the AI tracker + confirmation screen show the
  // real fields, not the layout containers.
  const formFields = flattenFormFields(
    (schema?.schemaJson as { components?: Parameters<typeof flattenFormFields>[0] } | undefined)?.components,
  );

  // Prefill legacy {{token}} defaultValues from the captured identity, then wire
  // file components to the upload endpoint. The server's public GET is sweep-only
  // (unknown tokens blanked, known identity tokens survive) so the client fills
  // them here. STABLE deps: `submitter` is a fresh object only at the identity
  // commit, so this re-fires once at that transition — never per keystroke (a
  // mid-input re-wire would drop in-progress Form.io input).
  const wiredPublicSchema = useMemo(() => {
    if (!schema?.schemaJson || !locationId) return schema?.schemaJson;
    const prefilled = substituteIdentityTokens(schema.schemaJson, submitter);
    return wireFormForUpload(prefilled, schema.id, locationId);
  }, [schema?.schemaJson, schema?.id, locationId, submitter]);

  // Route date-family prefill (DOB) through Form.io submission data — a datetime widget
  // does NOT paint a schema defaultValue on load. Stable ref (memoized on the wired schema)
  // so Form.io doesn't reset in-progress input on re-render.
  const publicPrefill = useMemo(
    () => ({ data: collectPrefillData(wiredPublicSchema) }),
    [wiredPublicSchema],
  );

  const renderFormioOrFallback = (): React.ReactElement => {
    if (!schema) return <></>;
    if (FormComp && schema.schemaJson) {
      return (
        <div className="ci-formio-form" style={styles.formioWrap}>
          <FormComp
            form={wiredPublicSchema}
            submission={publicPrefill}
            onSubmit={handleFormSubmit}
            onError={() => scrollToFirstFormError()}
            options={{}}
          />
          {submitting && <p style={styles.muted}>Submitting…</p>}
        </div>
      );
    }
    const components = ((schema.schemaJson as { components?: { key: string; label: string }[] })?.components ?? []);
    return (
      <div style={styles.fallbackForm}>
        {components.map((c) => (
          <div key={c.key} style={styles.fieldGroup}>
            <label style={styles.label} htmlFor={`pf-${c.key}`}>{c.label}</label>
            <input
              id={`pf-${c.key}`}
              className="ci-focusable"
              style={styles.input}
              value={String(formData[c.key] ?? '')}
              onChange={(e) => setFormData({ ...formData, [c.key]: e.target.value })}
            />
          </div>
        ))}
        <button
          style={{ ...styles.submitBtn, opacity: submitting ? 0.7 : 1 }}
          onClick={() => handleFormSubmit({ data: formData })}
          disabled={submitting}
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    );
  };

  const renderForm = (): React.ReactElement => {
    if (!schema || !locationId) return <></>;
    const showChat = mode === 'ai' && schema.aiMode !== 'form_only';
    return (
      <>
        {formError && <div style={styles.errorBox} role="alert">{formError}</div>}

        {schema.aiMode === 'toggle' && (
          <div style={styles.modeToggle}>
            <button
              className="ci-mode-btn"
              data-active={mode === 'ai'}
              style={{ ...styles.modeBtn, ...(mode === 'ai' ? styles.modeBtnActive : {}) }}
              onClick={() => setMode('ai')}
            >
              Chat with AI
            </button>
            <button
              className="ci-mode-btn"
              data-active={mode === 'manual'}
              style={{ ...styles.modeBtn, ...(mode === 'manual' ? styles.modeBtnActive : {}) }}
              onClick={() => setMode('manual')}
            >
              Fill form myself
            </button>
          </div>
        )}

        {showChat ? (
          <AIIntakeChat
            formId={schema.id}
            locationId={locationId}
            grant={grant ?? undefined}
            formFields={formFields}
            tenantName={schema.name}
            onSubmit={(data) => { void handleFormSubmit({ data }); }}
            onSwitchToForm={schema.aiMode === 'toggle' ? (collected) => { setFormData(collected); setMode('manual'); } : undefined}
          />
        ) : (
          renderFormioOrFallback()
        )}
      </>
    );
  };

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  const banner = (
    <div style={styles.secureBanner}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <rect x="3" y="11" width="18" height="11" rx="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
      Secure &amp; Encrypted
    </div>
  );

  const trustFooter = (
    <div style={styles.trustFooter}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9e9eaa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
      Your information is encrypted end-to-end
    </div>
  );

  if (screen.type === 'loading') {
    return (
      <div className="ci-portal" style={styles.page}>
        {banner}
        <div style={styles.container}><p style={styles.muted}>Loading form…</p></div>
      </div>
    );
  }

  if (screen.type === 'error') {
    return (
      <div className="ci-portal" style={styles.page}>
        {banner}
        <div style={styles.container}>
          <div className="ci-enter" style={styles.gateCard}>
            <span style={styles.errorIcon} aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </span>
            <h2 style={styles.cardTitle}>Something went wrong</h2>
            <p style={styles.cardSub}>{screen.message}</p>
            <p style={styles.errorFooter}>If you think this is a mistake, contact the office directly.</p>
          </div>
        </div>
      </div>
    );
  }

  if (screen.type === 'success') {
    return (
      <div className="ci-portal" style={styles.page}>
        {banner}
        <div style={styles.container}>
          <div className="ci-enter" style={styles.successCard}>
            <div style={styles.successIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--ci-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <h2 style={styles.tyTitle}>Thank you</h2>
            <p style={styles.tySub}>Your information has been submitted securely. The office has received it and will be in touch if anything else is needed.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ci-portal" style={styles.page}>
      {banner}
      <div style={styles.container}>
        {schema && (
          <div style={styles.titleBlock}>
            <h1 style={styles.formTitle}>{schema.name}</h1>
            {schema.description && <p style={styles.formDesc}>{schema.description}</p>}
          </div>
        )}

        {screen.type === 'identity' && (
          <div className="ci-enter" style={styles.gateCard}>
            <h2 style={styles.cardTitle}>{otpKey ? 'Verify your identity' : 'Your information'}</h2>
            <p style={styles.cardSub}>
              {otpKey
                ? (otpKey === 'email'
                    ? 'Enter your details to receive a verification code by email.'
                    : 'Enter your details to receive a verification code by text.')
                : 'Please provide the following information to begin.'}
            </p>
            {rows.map((row) => (
              <div key={row.key} style={styles.fieldGroup}>
                <label style={styles.label} htmlFor={`pf-${row.key}`}>
                  {labelFor(row.key)}
                  {!row.required && <span style={styles.optional}> (optional)</span>}
                </label>
                <input
                  id={`pf-${row.key}`}
                  className="ci-focusable"
                  type={INPUT_TYPE[row.key]}
                  autoComplete={AUTOCOMPLETE[row.key]}
                  max={row.key === 'dob' ? maxDob : undefined}
                  style={styles.input}
                  value={values[row.key] ?? ''}
                  onChange={(e) => setValues((prev) => ({ ...prev, [row.key]: e.target.value }))}
                  placeholder={PLACEHOLDER[row.key] ?? ''}
                  onKeyDown={(e) => { if (e.key === 'Enter') void handleIdentitySubmit(); }}
                />
              </div>
            ))}
            <p style={styles.reassure}>We use this only to verify it&apos;s you. Your information is encrypted.</p>
            {identityError && <div style={styles.errorBox} role="alert">{identityError}</div>}
            <button
              style={{ ...styles.submitBtn, opacity: requesting ? 0.7 : 1 }}
              onClick={() => void handleIdentitySubmit()}
              disabled={requesting}
              aria-busy={requesting}
            >
              {requesting ? 'Sending…' : (otpKey ? 'Send Verification Code' : 'Continue')}
            </button>
          </div>
        )}

        {screen.type === 'otp' && (
          <OtpEntry
            channelLabel={otpKey ?? 'email'}
            masked={maskedContact}
            onVerify={handleVerifyOtp}
            onResend={handleResendOtp}
          />
        )}

        {screen.type === 'form' && renderForm()}

        {trustFooter}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OtpEntry — auth-agnostic 6-digit entry (visual pattern lifted from
// portal/OTPVerifyScreen). Inlined here because the public form path is owned
// by this lane; a shared otp/OtpEntry.tsx extraction is flagged as follow-up.
// ---------------------------------------------------------------------------

function OtpEntry({
  onVerify,
  onResend,
  masked,
  channelLabel,
}: {
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  masked?: string;
  channelLabel: 'email' | 'phone';
}): React.ReactElement {
  const [digits,     setDigits]     = useState<string[]>(Array(6).fill(''));
  const [error,      setError]      = useState<string | null>(null);
  const [loading,    setLoading]    = useState(false);
  const [resendSecs, setResendSecs] = useState(RESEND_COOLDOWN);
  const [focusedIdx, setFocusedIdx] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (resendSecs <= 0) return;
    const t = setTimeout(() => setResendSecs((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendSecs]);

  const submit = useCallback(async (code: string) => {
    setLoading(true);
    setError(null);
    try {
      await onVerify(code);
    } catch (err) {
      setError((err as Error).message || 'Invalid code. Please try again.');
      setDigits(Array(6).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  }, [onVerify]);

  const handleDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < 5) inputRefs.current[index + 1]?.focus();
    if (digit && next.every((d) => d !== '')) void submit(next.join(''));
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = [...digits];
    for (let i = 0; i < 6; i++) next[i] = pasted[i] ?? '';
    setDigits(next);
    const lastIndex = Math.min(pasted.length - 1, 5);
    inputRefs.current[lastIndex]?.focus();
    if (pasted.length === 6) void submit(pasted);
  };

  const handleSubmitClick = () => {
    const code = digits.join('');
    if (code.length !== 6) { setError('Please enter all 6 digits'); return; }
    void submit(code);
  };

  const handleResend = async () => {
    setResendSecs(RESEND_COOLDOWN);
    setError(null);
    try {
      await onResend();
    } catch {
      setError('Failed to send a new code. Please try again.');
      setResendSecs(0);
    }
  };

  return (
    <div className="ci-enter" style={otpStyles.card}>
      <div style={otpStyles.iconWrap}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--ci-primary)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="11" width="18" height="11" rx="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      </div>

      <h1 style={otpStyles.title}>Verify Your Identity</h1>
      <p style={otpStyles.subtitle}>
        We sent a 6-digit code to your {channelLabel}.{' '}
        {masked ? <strong style={{ color: '#1a2744' }}>{masked}</strong> : null} Enter it below to continue.
      </p>

      <div style={otpStyles.digitRow} onPaste={handlePaste}>
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => { inputRefs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            className={`ci-digit-${i}`}
            style={{
              ...otpStyles.digitInput,
              ...(loading ? otpStyles.digitDisabled : {}),
              ...(digit ? otpStyles.digitFilled : {}),
              ...(focusedIdx === i ? otpStyles.digitFocused : {}),
            }}
            onChange={(e) => handleDigitChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onFocus={() => setFocusedIdx(i)}
            onBlur={() => setFocusedIdx(null)}
            disabled={loading}
            aria-label={`Digit ${i + 1} of 6`}
          />
        ))}
      </div>

      {error && <div style={otpStyles.error} role="alert">{error}</div>}

      <button
        style={{ ...otpStyles.submitBtn, opacity: loading ? 0.7 : 1 }}
        onClick={handleSubmitClick}
        disabled={loading}
        aria-busy={loading}
      >
        {loading ? 'Verifying…' : 'Continue'}
      </button>

      <div style={otpStyles.resendRow}>
        {resendSecs > 0 ? (
          <span style={otpStyles.resendWait}>Resend in {resendSecs}s</span>
        ) : (
          <button style={otpStyles.resendBtn} onClick={() => void handleResend()}>
            Send a new code
          </button>
        )}
      </div>

      <div style={otpStyles.secureNote}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#9e9eaa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        End-to-end encrypted
      </div>
    </div>
  );
}

const F = 'var(--ci-font)';

const styles: Record<string, React.CSSProperties> = {
  page:        { minHeight: '100vh', background: 'var(--ci-surface)', fontFamily: F },
  secureBanner: {
    background:     '#1a2744',
    color:          'rgba(255,255,255,0.82)',
    padding:        '9px 20px',
    fontSize:       12,
    textAlign:      'center',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            6,
    letterSpacing:  '0.01em',
    fontFamily:     F,
  },
  container:   { maxWidth: 1200, margin: '0 auto', padding: '24px 16px 48px' },
  titleBlock:  { marginBottom: 20 },
  formTitle:   { fontSize: 22, fontWeight: 700, color: '#1a2744', margin: '0 0 8px', fontFamily: F },
  formDesc:    { fontSize: 15, color: 'var(--ci-text-muted)', lineHeight: 1.6, margin: 0, fontFamily: F },
  gateCard: {
    background:   '#fff',
    borderRadius: 16,
    border:       '1px solid var(--ci-border)',
    padding:      '28px 24px',
    maxWidth:     440,
    width:        '100%',
    margin:       '0 auto',
    textAlign:    'center',
    fontFamily:   F,
  },
  successCard: {
    background:   '#fff',
    borderRadius: 16,
    border:       '1px solid var(--ci-border)',
    padding:      '40px 28px',
    maxWidth:     440,
    width:        '100%',
    margin:       '0 auto',
    textAlign:    'center',
    fontFamily:   F,
  },
  successIcon: {
    width:          72,
    height:         72,
    borderRadius:   '50%',
    background:     'var(--ci-primary-light)',
    border:         '1px solid #9dcfc9',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    margin:         '0 auto 16px',
  },
  errorIcon:   { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  cardTitle:   { fontSize: 18, fontWeight: 700, color: 'var(--ci-text)', margin: '0 0 8px', fontFamily: F },
  cardSub:     { fontSize: 14, color: 'var(--ci-text-muted)', lineHeight: 1.6, margin: '0 0 20px', fontFamily: F },
  errorFooter: { fontSize: 12, color: 'var(--ci-text-muted)', margin: '16px 0 0', fontFamily: F },
  reassure:    { fontSize: 13, color: 'var(--ci-text-muted)', margin: '0 0 14px', textAlign: 'left', lineHeight: 1.5, fontFamily: F },
  muted:       { color: 'var(--ci-text-muted)', fontSize: 14, fontFamily: F },
  tyTitle:     { fontSize: 24, fontWeight: 700, color: 'var(--ci-text)', margin: '0 0 8px', fontFamily: F },
  tySub:       { fontSize: 16, color: 'var(--ci-text-muted)', margin: 0, maxWidth: 360, lineHeight: 1.6, marginLeft: 'auto', marginRight: 'auto', fontFamily: F },
  fieldGroup:  { marginBottom: 14, textAlign: 'left' },
  label:       { display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--ci-text-body)', marginBottom: 5, fontFamily: F },
  optional:    { color: 'var(--ci-text-muted)', fontWeight: 400 },
  input: {
    width:        '100%',
    minHeight:    46,
    padding:      '12px 12px',
    border:       '1px solid var(--ci-border)',
    borderRadius: 8,
    fontSize:     15,
    color:        'var(--ci-text)',
    boxSizing:    'border-box' as const,
    outline:      'none',
    fontFamily:   F,
  },
  errorBox: {
    background:   '#fef4f2',
    border:       '1px solid #f5c6bc',
    borderRadius: 8,
    padding:      '10px 14px',
    color:        '#c0392b',
    fontSize:     13,
    marginBottom: 12,
    textAlign:    'left',
    fontFamily:   F,
  },
  submitBtn: {
    width:         '100%',
    minHeight:     46,
    padding:       '13px',
    background:    'var(--ci-primary)',
    color:         '#fff',
    border:        'none',
    borderRadius:  8,
    fontSize:      15,
    fontWeight:    600,
    cursor:        'pointer',
    marginTop:     4,
    fontFamily:    F,
    letterSpacing: '0.01em',
  },
  modeToggle:  { display: 'flex', gap: 8, marginBottom: 20 },
  modeBtn: {
    flex:         1,
    padding:      '12px',
    minHeight:    44,
    border:       '2px solid var(--ci-border)',
    borderRadius: 8,
    background:   '#ffffff',
    cursor:       'pointer',
    fontSize:     14,
    fontWeight:   500,
    color:        'var(--ci-text-body)',
    fontFamily:   F,
    transition:   'border-color 0.15s, color 0.15s, background 0.15s',
  },
  modeBtnActive: {
    borderColor: 'var(--ci-primary)',
    color:       'var(--ci-primary)',
    background:  'var(--ci-primary-light)',
  },
  formioWrap:  { background: '#fff', borderRadius: 14, padding: '24px', border: '1px solid var(--ci-border)' },
  fallbackForm: { background: '#fff', borderRadius: 14, padding: '24px', border: '1px solid var(--ci-border)' },
  trustFooter: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            5,
    marginTop:      24,
    fontSize:       12,
    color:          'var(--ci-text-muted)',
    fontFamily:     F,
  },
};

const otpStyles: Record<string, React.CSSProperties> = {
  card: {
    background:   '#ffffff',
    borderRadius: 16,
    padding:      '40px 32px',
    maxWidth:     440,
    width:        '100%',
    margin:       '0 auto',
    border:       '1px solid var(--ci-border)',
    textAlign:    'center',
    fontFamily:   F,
  },
  iconWrap: {
    width:          56, height: 56,
    borderRadius:   '50%',
    background:     'var(--ci-primary-light)',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    margin:         '0 auto 24px',
  },
  title:    { margin: '0 0 10px', fontSize: 22, fontWeight: 700, color: 'var(--ci-text)', fontFamily: F },
  subtitle: { margin: '0 0 28px', fontSize: 15, color: 'var(--ci-text-muted)', lineHeight: 1.65, fontFamily: F },
  digitRow: { display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 20 },
  digitInput: {
    width:        46, height: 54,
    border:       '2px solid var(--ci-border)',
    borderRadius: 10,
    fontSize:     22,
    fontWeight:   700,
    textAlign:    'center',
    color:        'var(--ci-text)',
    outline:      'none',
    transition:   'border-color 0.15s, background 0.15s',
    fontFamily:   F,
  },
  digitFilled:   { borderColor: 'var(--ci-primary)', background: 'var(--ci-primary-light)' },
  digitFocused:  { borderColor: 'var(--ci-primary)', boxShadow: '0 0 0 3px var(--ci-primary-focus)', outline: 'none' },
  digitDisabled: { background: 'var(--ci-border-light)', color: 'var(--ci-text-muted)' },
  error: {
    background:   '#fef4f2',
    border:       '1px solid #f5c6bc',
    borderRadius: 8,
    padding:      '10px 16px',
    color:        '#c0392b',
    fontSize:     14,
    marginBottom: 16,
    textAlign:    'left',
    fontFamily:   F,
  },
  submitBtn: {
    width:        '100%',
    minHeight:    46,
    padding:      '13px',
    background:   'var(--ci-primary)',
    color:        '#fff',
    border:       'none',
    borderRadius: 8,
    fontSize:     15,
    fontWeight:   600,
    cursor:       'pointer',
    marginBottom: 16,
    fontFamily:   F,
    letterSpacing: '0.01em',
  },
  resendRow:  { marginBottom: 28 },
  resendWait: { fontSize: 13, color: 'var(--ci-text-muted)', fontFamily: F },
  resendBtn: {
    background:     'none',
    border:         'none',
    color:          'var(--ci-primary)',
    cursor:         'pointer',
    fontSize:       13,
    fontWeight:     500,
    textDecoration: 'underline',
    fontFamily:     F,
  },
  secureNote: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            5,
    fontSize:       11,
    color:          'var(--ci-text-muted)',
    lineHeight:     1.5,
    fontFamily:     F,
  },
};
